import { Tooltip, Typography, DialogActions, Button, Stack, Box } from '@mui/material';
import { Popup } from 'components/common/popup/Popup';
import useBundleTranslation from 'i18n';
import { ICustomService, IDomain } from 'store/conciergeSlice';
import IconMi from 'components/common/icon/IconMi';
import IconHandler from 'components/common/icon/IconHandler';
import { useMemo } from 'react';

interface IAuthRequiredPopupProps {
    open: boolean;
    onConfirm: () => void;
    onHide: () => void;
    domainTitle: string;
    authInfo?: ICustomService['authInfo'];
    serviceName?: string;
    availableDomains?: IDomain[];
}

export default function AuthRequiredPopup({
    open,
    onConfirm,
    onHide,
    domainTitle,
    authInfo,
    serviceName,
    availableDomains = [],
}: IAuthRequiredPopupProps) {
    const { t } = useBundleTranslation(['app/concierge/widget']);

    const otherDomainsWithSameTool = useMemo(() => {
        if (!serviceName || !availableDomains.length) {
            return [];
        }

        return availableDomains.filter((domain) => {
            if (domain.title === domainTitle) {
                return false;
            }
            return domain.customServices?.some((service) => service.name === serviceName);
        });
    }, [serviceName, availableDomains, domainTitle]);

    const customActionPanel = (
        <DialogActions sx={{ justifyContent: 'center', py: 2, px: 3 }}>
            <Stack direction="row" spacing={2}>
                <Button variant="contained" onClick={onConfirm}>
                    {t('auth_required.authorize_button')}
                </Button>
                <Button variant="outlined" color="neutral" onClick={onHide}>
                    {t('close')}
                </Button>
            </Stack>
        </DialogActions>
    );

    return (
        <Popup
            settings={{
                noTitle: true,
                customActionPanel: customActionPanel,
            }}
            open={open}
            onConfirm={onConfirm}
            onHide={onHide}
            maxWidth={'popupSm'}
        >
            <Stack spacing={1} alignItems="center" sx={{ pt: 2 }}>
                <Box sx={{ fontSize: '28px', color: 'primary.main' }}>
                    <IconMi icon="key" />
                </Box>
                <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>{t('footer.auth_required')}</Typography>
                <Typography sx={{ textAlign: 'center' }}>
                    {t('auth_required.login_to')}{' '}
                    <Box component="span" sx={{ fontWeight: 600 }}>
                        {authInfo?.providerName || domainTitle}
                    </Box>{' '}
                    {t('auth_required.to_ask_questions')}{' '}
                    <Box component="span" sx={{ fontWeight: 600 }}>
                        {domainTitle}
                    </Box>
                    . {authInfo?.remainDays && t('auth_required.expires_in', { days: authInfo.remainDays })}
                </Typography>
                {otherDomainsWithSameTool.length > 0 && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider', width: '100%' }}>
                        <Tooltip
                            title={t('auth_required.also_enables_access', {
                                provider: authInfo?.providerName || domainTitle,
                            })}
                        >
                            <Typography
                                sx={{
                                    fontSize: '12px',
                                    color: 'text.primary',
                                    opacity: 0.8,
                                    px: 1.5,
                                    py: 0.75,
                                    backgroundColor: 'divider',
                                    borderRadius: '3px',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {t('auth_required.also_enables_access', {
                                    provider: authInfo?.providerName || domainTitle,
                                })}
                            </Typography>
                        </Tooltip>
                        <Stack spacing={0} sx={{ ml: 1 }}>
                            {otherDomainsWithSameTool.map((domain, idx) => {
                                const iconProps = {
                                    type: domain.icon_ind,
                                    value:
                                        domain.icon_ind === 'upload'
                                            ? `data:image/png;base64,${domain.icon_data}`
                                            : domain.icon_name,
                                };

                                return (
                                    <Stack
                                        key={idx}
                                        direction="row"
                                        alignItems="center"
                                        spacing={1}
                                        sx={{
                                            py: 1,
                                            borderBottom: '1px solid',
                                            borderColor: 'divider',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: '21px',
                                                height: '21px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <IconHandler icon={iconProps} sx={{ fontSize: '16px' }} />
                                        </Box>
                                        <Typography sx={{ fontSize: '13px' }}>{domain.title}</Typography>
                                    </Stack>
                                );
                            })}
                        </Stack>
                    </Box>
                )}
            </Stack>
        </Popup>
    );
}
